import { DEFAULT_LOCALE, type Locale } from '../i18n/resources';
import { translateForLocale } from '../i18n/translate';

const GENERIC_RESPONSE_ERROR_MESSAGE = /^Response returned an error code$/i;

type ResponseLikeError = {
  name: string;
  message: string;
  response: Response;
};

type FetchLikeError = {
  name: string;
  message: string;
  cause: unknown;
};

function readLocaleFromDom(): Locale {
  const lang = document.documentElement.getAttribute('lang');
  if (lang === 'en' || lang === 'de') {
    return lang;
  }
  return DEFAULT_LOCALE;
}

function isResponseLikeError(error: unknown): error is ResponseLikeError {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as {
    name?: unknown;
    message?: unknown;
    response?: unknown;
  };

  return (
    candidate.name === 'ResponseError' &&
    typeof candidate.message === 'string' &&
    candidate.response instanceof Response
  );
}

export function getApiErrorHttpStatus(error: unknown): number | undefined {
  if (!isResponseLikeError(error)) {
    return undefined;
  }
  return error.response.status;
}

export function isForbiddenOrNotFoundApiError(error: unknown): boolean {
  const status = getApiErrorHttpStatus(error);
  return status === 403 || status === 404;
}

const TENANT_ASSERT_ACTIVE_PROPERTY_PATH =
  'TenantOperationalSupport.assertActive';

function isTenantDeactivatedFailure(failure: unknown): boolean {
  if (!failure || typeof failure !== 'object') {
    return false;
  }

  const record = failure as Record<string, unknown>;
  return (
    typeof record.propertyPath === 'string' &&
    record.propertyPath === TENANT_ASSERT_ACTIVE_PROPERTY_PATH
  );
}

function isTenantDeactivatedErrorBody(body: unknown): boolean {
  if (!body || typeof body !== 'object') {
    return false;
  }

  const failures = (body as Record<string, unknown>).failures;
  if (!Array.isArray(failures)) {
    return false;
  }

  return failures.some(isTenantDeactivatedFailure);
}

async function readResponseErrorJsonBody(
  error: ResponseLikeError,
): Promise<unknown | null> {
  try {
    const contentType = error.response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      return await error.response.clone().json();
    }

    const text = (await error.response.clone().text()).trim();
    if (text === '') {
      return null;
    }

    try {
      return JSON.parse(text) as unknown;
    } catch {
      return null;
    }
  } catch {
    return null;
  }
}

/**
 * True when the backend reports the caller's tenant is inactive
 * (`TenantOperationalSupport.assertActive`).
 */
export async function isTenantDeactivatedApiError(
  error: unknown,
): Promise<boolean> {
  if (!isResponseLikeError(error)) {
    return false;
  }

  const body = await readResponseErrorJsonBody(error);
  return isTenantDeactivatedErrorBody(body);
}

function isFetchLikeError(error: unknown): error is FetchLikeError {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as {
    name?: unknown;
    message?: unknown;
    cause?: unknown;
  };

  return (
    candidate.name === 'FetchError' &&
    typeof candidate.message === 'string' &&
    'cause' in candidate
  );
}

function messageFromUnknownJson(body: unknown): string | null {
  if (!body || typeof body !== 'object') {
    return null;
  }

  const record = body as Record<string, unknown>;
  for (const key of [
    'message',
    'errorMessage',
    'detail',
    'title',
    'error',
  ] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }

  const failures = record.failures;
  if (Array.isArray(failures)) {
    const messages = failures
      .map((failure) => {
        if (!failure || typeof failure !== 'object') {
          return null;
        }
        const message = (failure as Record<string, unknown>).message;
        return typeof message === 'string' && message.trim() !== ''
          ? message.trim()
          : null;
      })
      .filter((message): message is string => message != null);

    if (messages.length > 0) {
      return messages.join('; ');
    }
  }

  return null;
}

/**
 * Best-effort human-readable message from a failed API call.
 * Prefer this in mutation errorToast builders via `apiMessage`.
 *
 * Supports generated clients from multiple packages (`backend-svc-api`,
 * `lac-svc-api`, …) by duck-typing `ResponseError` / `FetchError`.
 */
export async function resolveApiErrorMessage(error: unknown): Promise<string> {
  if (isResponseLikeError(error)) {
    const body = await readResponseErrorJsonBody(error);
    const fromJson = messageFromUnknownJson(body);
    if (fromJson) {
      return fromJson;
    }

    try {
      const text = (await error.response.clone().text()).trim();
      if (text !== '') {
        return text;
      }
    } catch {
      // Fall through to status / Error.message.
    }

    if (
      error.message.trim() !== '' &&
      !GENERIC_RESPONSE_ERROR_MESSAGE.test(error.message)
    ) {
      return error.message.trim();
    }

    return translateForLocale(readLocaleFromDom(), 'toast.requestFailed', {
      status: String(error.response.status),
    });
  }

  if (isFetchLikeError(error)) {
    const causeMessage =
      error.cause instanceof Error ? error.cause.message.trim() : '';
    if (causeMessage !== '') {
      return causeMessage;
    }
    if (error.message.trim() !== '') {
      return error.message.trim();
    }
  }

  if (error instanceof Error && error.message.trim() !== '') {
    if (!GENERIC_RESPONSE_ERROR_MESSAGE.test(error.message)) {
      return error.message.trim();
    }
  }

  return '';
}
